import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Dropdown, message, Badge, Modal, Form, Input, Popover } from 'antd';
import adminLogo from '../../assets/images/adminlogo.png';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  BellOutlined,
  TeamOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  KeyOutlined,
  NotificationOutlined,
  AuditOutlined,
  DownloadOutlined,
  SolutionOutlined,
  IdcardOutlined,
  FormOutlined,
  ProfileOutlined,
  FileProtectOutlined,
  FileDoneOutlined,
  FileExclamationOutlined,
  FileUnknownOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import './admin.css';

const { Header, Sider, Content } = AntLayout;

interface AdminLayoutProps {
  children: React.ReactNode;
}

// 自定义菜单项类型，添加permission属性
interface CustomMenuItem {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  permission?: number;
  children?: CustomMenuItem[];
  type?: 'divider' | 'group';
  badgeCount?: number; // 添加badge计数，方便折叠时显示
}

// 权限编号映射
// 1: 仪表盘, 2: 招标信息管理, 3: 订单中心, 4: 信息发布, 5: 账号管理, 6: 后台管理, 7: 下载中心, 8: 开标大厅
const permissionMap: Record<string, number> = {
  'dashboard': 1,
  'bid-management': 2,
  'bid-tender': 2,
  'bid-change': 2,
  'bid-result': 2,
  'bid-other': 2,
  'order-center': 3,
  'order-management': 3,
  'supplier-registration': 3,
  'info-publish': 4,
  'info-company': 4,
  'info-industry': 4,
  'info-policy': 4,
  'account-management': 5,
  'account-supplier-audit': 5,
  'account-supplier': 5,
  'account-expert-audit': 5,
  'account-expert': 5,
  'account-purchaser': 5,
  'system-management': 6,
  'system-members': 6,
  'system-roles': 6,
  'system-feedback': 6,
  'system-download-records': 6,
  'download-center': 7,
  'open-bid-hall': 8,
  'today-open-bid': 8,
};

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 待审核数量
  const [pendingSupplierCount, setPendingSupplierCount] = useState(0);
  const [pendingExpertCount, setPendingExpertCount] = useState(0);
  // 待处理发票数量
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  
  // 处理 token 过期，自动跳转登录
  const handleTokenExpired = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    message.error('登录已过期，请重新登录');
    navigate('/admin/login');
  };

  // 全局API响应拦截器，处理token过期
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      // 检查是否是admin API请求且返回401
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      if (url.includes('/api/admin/') && response.status === 401) {
        handleTokenExpired();
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // 获取待审核数量和待处理发票数量
  useEffect(() => {
    const fetchPendingCounts = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) {
          handleTokenExpired();
          return;
        }
        
        // 获取待审核供应商数量
        const supplierRes = await fetch('/api/admin/users/pending', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (supplierRes.status === 401) {
          handleTokenExpired();
          return;
        }
        const supplierResp = await supplierRes.json();
        if (supplierResp?.ok) {
          setPendingSupplierCount(supplierResp.data?.length || 0);
        }
        
        // 获取待审核专家数量
        const expertRes = await fetch('/api/admin/experts/pending', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (expertRes.status === 401) {
          handleTokenExpired();
          return;
        }
        const expertResp = await expertRes.json();
        if (expertResp?.ok) {
          setPendingExpertCount(expertResp.data?.length || 0);
        }
        
        // 获取待处理发票数量
        const ordersRes = await fetch('/api/admin/trade-orders', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (ordersRes.status === 401) {
          handleTokenExpired();
          return;
        }
        const ordersResp = await ordersRes.json();
        if (ordersResp?.ok) {
          setPendingInvoiceCount(ordersResp.pendingInvoiceCount || 0);
        }
      } catch (err) {
        console.error('获取待审核数量失败:', err);
      }
    };
    
    fetchPendingCounts();
    // 每30秒刷新一次
    const interval = setInterval(fetchPendingCounts, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // 获取用户权限
  const adminUser = localStorage.getItem('admin_user');
  const userInfo = adminUser ? JSON.parse(adminUser) : null;
  const userPermissions: number[] = userInfo?.permission || [];
  const roleName = userInfo?.role_name || null;
  
  // 检查是否有权限
  const hasPermission = (permissionId: number) => {
    return userPermissions.includes(permissionId);
  };
  
  // 根据角色名称过滤订单中心子菜单
  const filterOrderCenterChildren = (children: CustomMenuItem[]): CustomMenuItem[] => {
    if (roleName === '财务人员') {
      // 财务人员：只显示订单管理
      return children.filter(item => item.key === 'order-management');
    } else if (roleName === '项目经理') {
      // 项目经理：只显示供应商报名清单和其他公告报名清单
      return children.filter(item => 
        item.key === 'supplier-registration' || item.key === 'other-announcement-registration'
      );
    }
    // 其他角色：显示所有子菜单
    return children;
  };
  
  // 从路径中获取当前选中的菜单项
  const pathParts = location.pathname.replace(/^\/admin\/?/, '').split('/');
  const selectedKey = pathParts.join('/') || 'dashboard';
  
  // 获取打开的子菜单键
  const getOpenKeys = () => {
    const keys: string[] = [];
    if (selectedKey.startsWith('bid-')) keys.push('bid-management');
    if (selectedKey.startsWith('order-') || selectedKey.startsWith('supplier-registration') || selectedKey.startsWith('other-announcement-registration')) keys.push('order-center');
    if (selectedKey.startsWith('info-')) keys.push('info-publish');
    if (selectedKey.startsWith('account-')) keys.push('account-management');
    if (selectedKey.startsWith('system-')) keys.push('system-management');
    if (selectedKey.startsWith('today-open-bid') || selectedKey.startsWith('open-bid-hall')) keys.push('open-bid-hall');
    return keys;
  };
  
  const [openKeys, setOpenKeys] = useState<string[]>(getOpenKeys());

  // 根据权限过滤菜单 - 用于展开状态
  const menuItems = useMemo(() => {
    const allMenuItems: CustomMenuItem[] = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined style={{ fontSize: 18 }} />,
        label: '数据看板',
        permission: 1,
      },
      {
        key: 'open-bid-hall',
        icon: <HomeOutlined style={{ fontSize: 18 }} />,
        label: '开标大厅',
        permission: 8,
        children: [
          {
            key: 'today-open-bid',
            icon: <ClockCircleOutlined style={{ fontSize: 16 }} />,
            label: '今日开标项目',
          },
        ],
      },
      {
        key: 'bid-management',
        icon: <FileTextOutlined style={{ fontSize: 18 }} />,
        label: '招标信息管理',
        permission: 2,
        children: [
          {
            key: 'bid-tender',
            icon: <FormOutlined style={{ fontSize: 16 }} />,
            label: '招标公告',
          },
          // {
          //   key: 'bid-change',
          //   icon: <FileExclamationOutlined style={{ fontSize: 16 }} />,
          //   label: '变更公告',
          // },
          {
            key: 'bid-result',
            icon: <FileDoneOutlined style={{ fontSize: 16 }} />,
            label: '中标公告',
          },
          {
            key: 'bid-other',
            icon: <FileUnknownOutlined style={{ fontSize: 16 }} />,
            label: '其他公告',
          },
        ],
      },
      {
        key: 'order-center',
        icon: <ShoppingCartOutlined style={{ fontSize: 18 }} />,
        label: pendingInvoiceCount > 0 ? (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            订单中心
            <Badge count={pendingInvoiceCount} size="small" style={{ marginLeft: 6 }} />
          </span>
        ) : '订单中心',
        permission: 3,
        children: filterOrderCenterChildren([
          {
            key: 'order-management',
            icon: <ShoppingCartOutlined style={{ fontSize: 16 }} />,
            label: pendingInvoiceCount > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                订单管理
                <Badge count={pendingInvoiceCount} size="small" style={{ marginLeft: 6 }} />
              </span>
            ) : '订单管理',
          },
          {
            key: 'supplier-registration',
            icon: <SolutionOutlined style={{ fontSize: 16 }} />,
            label: '供应商报名清单',
          },
          {
            key: 'other-announcement-registration',
            icon: <FileTextOutlined style={{ fontSize: 16 }} />,
            label: '其他公告报名清单',
          },
        ]),
      },
      {
        key: 'info-publish',
        icon: <NotificationOutlined style={{ fontSize: 18 }} />,
        label: '信息发布',
        permission: 4,
        children: [
          {
            key: 'info-company',
            icon: <ProfileOutlined style={{ fontSize: 16 }} />,
            label: '公司新闻',
          },
          {
            key: 'info-industry',
            icon: <FileProtectOutlined style={{ fontSize: 16 }} />,
            label: '行业动态',
          },
          {
            key: 'info-policy',
            icon: <AuditOutlined style={{ fontSize: 16 }} />,
            label: '政策法规',
          },
        ],
      },
      {
        key: 'account-management',
        icon: <TeamOutlined style={{ fontSize: 18 }} />,
        label: (pendingSupplierCount + pendingExpertCount) > 0 ? (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            账号管理
            <Badge count={pendingSupplierCount + pendingExpertCount} size="small" style={{ marginLeft: 6 }} />
          </span>
        ) : '账号管理',
        permission: 5,
        children: [
          {
            key: 'account-supplier-audit',
            icon: <AuditOutlined style={{ fontSize: 16 }} />,
            label: pendingSupplierCount > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                供应商审核
                <Badge count={pendingSupplierCount} size="small" style={{ marginLeft: 6 }} />
              </span>
            ) : '供应商审核',
          },
          {
            key: 'account-supplier',
            icon: <SolutionOutlined style={{ fontSize: 16 }} />,
            label: '供应商管理',
          },
          {
            key: 'account-expert-audit',
            icon: <AuditOutlined style={{ fontSize: 16 }} />,
            label: pendingExpertCount > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                评审人员审核
                <Badge count={pendingExpertCount} size="small" style={{ marginLeft: 6 }} />
              </span>
            ) : '评审人员审核',
          },
          {
            key: 'account-expert',
            icon: <IdcardOutlined style={{ fontSize: 16 }} />,
            label: '评审人员管理',
          },
          {
            key: 'account-purchaser',
            icon: <UserOutlined style={{ fontSize: 16 }} />,
            label: '采购人管理',
          },
        ],
      },
      {
        key: 'system-management',
        icon: <SettingOutlined style={{ fontSize: 18 }} />,
        label: '后台管理',
        permission: 6,
        children: [
          {
            key: 'system-members',
            icon: <TeamOutlined style={{ fontSize: 16 }} />,
            label: '后台管理',
          },
          {
            key: 'system-roles',
            icon: <SafetyCertificateOutlined style={{ fontSize: 16 }} />,
            label: '角色管理',
          },
          {
            key: 'system-feedback',
            icon: <MessageOutlined style={{ fontSize: 16 }} />,
            label: '信息反馈',
          },
          {
            key: 'system-download-records',
            icon: <DownloadOutlined style={{ fontSize: 16 }} />,
            label: '下载记录',
          },
        ],
      },
      {
        key: 'download-center',
        icon: <DownloadOutlined style={{ fontSize: 18 }} />,
        label: '下载中心',
        permission: 7,
      },
    ];
    
    // 根据权限过滤菜单
    return allMenuItems.filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    }) as MenuProps['items'];
  }, [userPermissions, pendingSupplierCount, pendingExpertCount, pendingInvoiceCount, roleName]);

  // 折叠状态下的菜单数据（用于自定义渲染popup子菜单）
  const collapsedMenuData = useMemo(() => {
    return [
      {
        key: 'dashboard',
        icon: <DashboardOutlined style={{ fontSize: 18 }} />,
        label: '数据看板',
        permission: 1,
      },
      {
        key: 'open-bid-hall',
        icon: <HomeOutlined style={{ fontSize: 18 }} />,
        label: '开标大厅',
        permission: 8,
        children: [
          { key: 'today-open-bid', icon: <ClockCircleOutlined style={{ fontSize: 16 }} />, label: '今日开标项目' },
        ],
      },
      {
        key: 'bid-management',
        icon: <FileTextOutlined style={{ fontSize: 18 }} />,
        label: '招标信息管理',
        permission: 2,
        children: [
          { key: 'bid-tender', icon: <FormOutlined style={{ fontSize: 16 }} />, label: '招标公告' },
          // { key: 'bid-change', icon: <FileExclamationOutlined style={{ fontSize: 16 }} />, label: '变更公告' },
          { key: 'bid-result', icon: <FileDoneOutlined style={{ fontSize: 16 }} />, label: '中标公告' },
          { key: 'bid-other', icon: <FileUnknownOutlined style={{ fontSize: 16 }} />, label: '其他公告' },
        ],
      },
      {
        key: 'order-center',
        icon: <ShoppingCartOutlined style={{ fontSize: 18 }} />,
        label: '订单中心',
        permission: 3,
        badgeCount: pendingInvoiceCount,
        children: filterOrderCenterChildren([
          { key: 'order-management', icon: <ShoppingCartOutlined style={{ fontSize: 16 }} />, label: '订单管理', badgeCount: pendingInvoiceCount },
          { key: 'supplier-registration', icon: <SolutionOutlined style={{ fontSize: 16 }} />, label: '供应商报名清单' },
          { key: 'other-announcement-registration', icon: <FileTextOutlined style={{ fontSize: 16 }} />, label: '其他公告报名清单' },
        ]),
      },
      {
        key: 'info-publish',
        icon: <NotificationOutlined style={{ fontSize: 18 }} />,
        label: '信息发布',
        permission: 4,
        children: [
          { key: 'info-company', icon: <ProfileOutlined style={{ fontSize: 16 }} />, label: '公司新闻' },
          { key: 'info-industry', icon: <FileProtectOutlined style={{ fontSize: 16 }} />, label: '行业动态' },
          { key: 'info-policy', icon: <AuditOutlined style={{ fontSize: 16 }} />, label: '政策法规' },
        ],
      },
      {
        key: 'account-management',
        icon: <TeamOutlined style={{ fontSize: 18 }} />,
        label: '账号管理',
        permission: 5,
        badgeCount: pendingSupplierCount + pendingExpertCount,
        children: [
          { key: 'account-supplier-audit', icon: <AuditOutlined style={{ fontSize: 16 }} />, label: '供应商审核', badgeCount: pendingSupplierCount },
          { key: 'account-supplier', icon: <SolutionOutlined style={{ fontSize: 16 }} />, label: '供应商管理' },
          { key: 'account-expert-audit', icon: <AuditOutlined style={{ fontSize: 16 }} />, label: '评审人员审核', badgeCount: pendingExpertCount },
          { key: 'account-expert', icon: <IdcardOutlined style={{ fontSize: 16 }} />, label: '评审人员管理' },
          { key: 'account-purchaser', icon: <UserOutlined style={{ fontSize: 16 }} />, label: '采购人管理' },
        ],
      },
      {
        key: 'system-management',
        icon: <SettingOutlined style={{ fontSize: 18 }} />,
        label: '后台管理',
        permission: 6,
        children: [
          { key: 'system-members', icon: <TeamOutlined style={{ fontSize: 16 }} />, label: '后台管理' },
          { key: 'system-roles', icon: <SafetyCertificateOutlined style={{ fontSize: 16 }} />, label: '角色管理' },
          { key: 'system-feedback', icon: <MessageOutlined style={{ fontSize: 16 }} />, label: '信息反馈' },
          { key: 'system-download-records', icon: <DownloadOutlined style={{ fontSize: 16 }} />, label: '下载记录' },
        ],
      },
      {
        key: 'download-center',
        icon: <DownloadOutlined style={{ fontSize: 18 }} />,
        label: '下载中心',
        permission: 7,
      },
    ].filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [userPermissions, pendingSupplierCount, pendingExpertCount, pendingInvoiceCount, roleName]);

  // 渲染折叠状态下的子菜单Popover内容
  const renderCollapsedSubmenu = (item: typeof collapsedMenuData[0]) => {
    if (!item.children) return null;
    
    return (
      <div className="collapsed-submenu-popup">
        <div className="collapsed-submenu-title">{item.label}</div>
        <div className="collapsed-submenu-items">
          {item.children.map((child) => (
            <div
              key={child.key}
              className={`collapsed-submenu-item ${selectedKey === child.key ? 'active' : ''}`}
              onClick={() => {
                navigate(`/admin/${child.key}`);
              }}
            >
              <span className="collapsed-submenu-item-icon">{child.icon}</span>
              <span className="collapsed-submenu-item-label">{child.label}</span>
              {child.badgeCount && child.badgeCount > 0 && (
                <Badge count={child.badgeCount} size="small" style={{ marginLeft: 'auto' }} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 折叠状态下的自定义菜单渲染
  const renderCollapsedMenu = () => {
    return (
      <div className="collapsed-menu-container">
        {collapsedMenuData.map((item) => {
          const isSelected = selectedKey === item.key || 
            (item.children && item.children.some(child => child.key === selectedKey));
          
          // 有子菜单的项目使用Popover
          if (item.children) {
            return (
              <Popover
                key={item.key}
                placement="right"
                content={renderCollapsedSubmenu(item)}
                trigger="hover"
                overlayClassName="collapsed-menu-popover"
                arrow={false}
              >
                <div className={`collapsed-menu-item ${isSelected ? 'selected' : ''}`}>
                  {item.badgeCount && item.badgeCount > 0 ? (
                    <Badge count={item.badgeCount} size="small" offset={[6, -4]}>
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </div>
              </Popover>
            );
          }
          
          // 没有子菜单的项目直接点击跳转
          return (
            <div
              key={item.key}
              className={`collapsed-menu-item ${isSelected ? 'selected' : ''}`}
              onClick={() => navigate(`/admin/${item.key}`)}
            >
              {item.icon}
            </div>
          );
        })}
      </div>
    );
  };

  // 获取当前页面标题
  const getPageTitle = () => {
    const titleMap: Record<string, string> = {
      'dashboard': '数据看板',
      'bid-tender': '招标公告',
      'bid-change': '变更公告',
      'bid-result': '中标公告',
      'bid-other': '其他公告',
      'order-management': '订单管理',
      'supplier-registration': '供应商报名清单',
      'info-company': '公司新闻',
      'info-industry': '行业动态',
      'info-policy': '政策法规',
      'account-supplier-audit': '供应商审核',
      'account-supplier': '供应商管理',
      'account-expert-audit': '评审人员审核',
      'account-expert': '评审人员管理',
      'account-purchaser': '采购人管理',
      'system-members': '后台管理',
      'system-roles': '角色管理',
      'system-feedback': '信息反馈',
      'system-download-records': '下载记录',
      'download-center': '下载中心',
      'today-open-bid': '今日开标项目',
    };
    return titleMap[selectedKey] || '数据看板';
  };

  // 获取子菜单的第一个子项（根据角色动态选择）
  const getSubmenuFirstChild = (parentKey: string): string | null => {
    const submenuFirstChildMap: Record<string, string> = {
      'bid-management': 'bid-tender',
      'info-publish': 'info-company',
      'account-management': 'account-supplier-audit',
      'system-management': 'system-members',
      'open-bid-hall': 'today-open-bid',
    };
    
    // 订单中心需要根据角色动态选择第一个子菜单
    if (parentKey === 'order-center') {
      if (roleName === '财务人员') {
        return 'order-management';
      } else if (roleName === '项目经理') {
        return 'supplier-registration';
      } else {
        return 'order-management';
      }
    }
    
    return submenuFirstChildMap[parentKey] || null;
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    // 检查是否有权限访问
    const requiredPermission = permissionMap[key];
    if (requiredPermission && !hasPermission(requiredPermission)) {
      message.error('您没有权限访问该页面');
      return;
    }
    
    // 如果是折叠状态且点击的是有子菜单的项，展开侧边栏并跳转到第一个子菜单
    const firstChild = getSubmenuFirstChild(key);
    if (collapsed && firstChild) {
      setCollapsed(false);
      setOpenKeys([key]);
      navigate(`/admin/${firstChild}`);
      return;
    }
    
    navigate(`/admin/${key}`);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    message.success('已退出登录');
    navigate('/admin/login');
  };

  // 密码修改弹窗
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      
      if (values.newPassword !== values.confirmPassword) {
        message.error('两次输入的新密码不一致');
        return;
      }
      
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(resp?.error || '密码修改失败');
      }
    } catch (err) {
      // 表单验证失败
    } finally {
      setSubmitting(false);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'change-password',
      icon: <KeyOutlined />,
      label: '密码修改',
      onClick: () => setPasswordModalVisible(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 获取角色显示名称
  const getRoleName = () => {
    if (userPermissions.length === 7) return '系统管理员';
    if (userPermissions.length === 1 && userPermissions[0] === 5) return '子账户管理员';
    if (userPermissions.length === 1 && userPermissions[0] === 3) return '审核专员';
    return '管理员';
  };

  return (
    <AntLayout className="admin-layout">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed} 
        width={240}
        collapsedWidth={80}
      >
        {/* Logo区域 */}
        <div className="admin-logo">
          <div className="admin-logo-icon">
            
          </div>
          {!collapsed && <span>德润慧通管理后台</span>}
        </div>
        
        {/* 菜单 - 折叠时使用自定义渲染，展开时使用Ant Design Menu */}
        {collapsed ? (
          renderCollapsedMenu()
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ 
              marginTop: 8,
              borderRight: 'none'
            }}
          />
        )}
      </Sider>
      
      <AntLayout>
        {/* 头部 */}
        <Header className="admin-header">
          <div className="admin-header-left">
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
            })}
            <span className="admin-header-title">{getPageTitle()}</span>
          </div>
          
          <div className="admin-header-right">
            {/* 用户信息 */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                background: 'white',
                transition: 'all 0.2s'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <UserOutlined style={{ color: 'white', fontSize: '18px' }} />
                </div>
                <div style={{ lineHeight: 1.3 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    {userInfo?.username || '管理员'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {getRoleName()}
                  </div>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        {/* 内容区域 */}
        <Content className="admin-content">
          {children}
        </Content>
      </AntLayout>
      
      {/* 密码修改弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <KeyOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>修改密码</span>
          </div>
        }
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        onOk={handleChangePassword}
        okText="确认修改"
        cancelText="取消"
        confirmLoading={submitting}
        width={400}
        centered
        destroyOnHidden
      >
        <Form form={passwordForm} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>
          
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6位' },
              { max: 20, message: '密码长度不能超过20位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（6-20位）" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[
              { required: true, message: '请再次输入新密码' },
              { min: 6, message: '密码长度不能少于6位' },
              { max: 20, message: '密码长度不能超过20位' },
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </AntLayout>
  );
}
